// Bolt Checkout gateway.
// Reference: https://help.bolt.com/products/checkout/
//
// Bolt's embedded checkout is the primary flow: the BFF (or Suitelet)
// creates a Bolt cart server-side and returns a token, then the SPA
// loads `connect.js`, opens the embedded checkout in a container, and
// handles success/close callbacks.
//
// Auth-only vs auth+capture in Bolt:
//   * Bolt itself controls auth/capture timing via the merchant config
//     and the `transaction_type` on the cart. We pass `capture` through
//     to the BFF so it can set the right value when creating the cart;
//     the SPA-side widget code is identical either way.

import { config } from "../config";
import { api } from "../api/client";

const BOLT_SCRIPT = "https://connect.bolt.com/connect.js";

let _sdkPromise = null;

export const boltGateway = {
  id: "bolt",
  label: "Bolt Checkout",
  features: {
    walletButtons: true,
    embeddedForm: true,
    hosted: false,
    savedCards: true,
  },

  async loadSdk() {
    if (typeof window === "undefined") return;
    if (window.BoltCheckout) return;
    if (_sdkPromise) return _sdkPromise;
    _sdkPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${BOLT_SCRIPT}"]`);
      if (existing) {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () => reject(new Error("Bolt SDK failed to load")));
        return;
      }
      const s = document.createElement("script");
      s.src = BOLT_SCRIPT;
      s.async = true;
      if (config.bolt?.publishableKey) {
        s.setAttribute("data-publishable-key", config.bolt.publishableKey);
      }
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("Bolt SDK failed to load"));
      document.head.appendChild(s);
    });
    return _sdkPromise;
  },

  async createIntent({ orderId, amount, currency = "USD", capture = true, customer, items, metadata }) {
    // The BFF (or embedded Suitelet) is the one that calls Bolt's
    // server API. We send the order context; it returns the Bolt token
    // the SPA needs to mount the checkout.
    if (typeof api.createBoltCart === "function") {
      const out = await api.createBoltCart({
        orderId, amount, currency, capture, customer, items, metadata,
      });
      return {
        token: out?.token || out?.boltToken,
        intentId: out?.cartId || out?.intentId || orderId,
        raw: out,
      };
    }
    // Fallback for dev — return a fake token so the UI flow can be
    // exercised without the backend. The mount step will refuse to
    // render unless the SDK is present, so this is safe.
    return { token: `dev-${Date.now()}`, intentId: orderId || `dev-${Date.now()}` };
  },

  async mount({ container, token, capture, on = {} }) {
    if (!container) throw new Error("container is required to mount Bolt");
    if (!config.bolt?.publishableKey) {
      // Dev safety: render a placeholder instead of throwing.
      container.innerHTML = `
        <div style="padding:24px;border:1px dashed rgba(0,0,0,0.18);border-radius:8px;color:#555;font-size:0.9rem;line-height:1.5">
          <strong>Bolt not configured.</strong><br/>
          Set <code>VITE_BOLT_PUBLISHABLE_KEY</code> and ensure the BFF can mint a Bolt cart token.
          <div style="margin-top:8px;color:#888">Capture mode: <code>${capture ? "auth + capture" : "auth only"}</code></div>
        </div>`;
      return;
    }
    if (!window.BoltCheckout) await this.loadSdk();
    try {
      window.BoltCheckout.configure({ publishableKey: config.bolt.publishableKey });
      window.BoltCheckout.open({
        token,
        containerEl: container,
        capture: !!capture, // forwarded; Bolt cart's transaction_type is set server-side
        onSuccess: (tx) => on.success?.(tx),
        onError:   (err) => on.error?.(err),
        onClose:   ()    => on.close?.(),
      });
    } catch (e) {
      on.error?.(e);
    }
  },

  unmount() {
    try { window.BoltCheckout?.close?.(); } catch { /* */ }
  },
};
